import { useCallback, useEffect, useRef } from 'react';

import { ATI_PRESETS } from '@atlassian/universal-create-common';

import { usePlatformCreateAnalytics } from '../../../../common';
import { getBaseFormControlAnalyticsAttributes } from '../../../townsquare/utils';

const JIRA_ITEM_PRODUCT = 'jira' as const;

/** Parameters for the Jira issue create analytics hook. */
export type UseGicIssueCreateAnalyticsParams = {
	isCrossAppCreate?: boolean;
	/** Whether the modal is currently open. The viewed event only fires when true. */
	isOpen?: boolean;
	product?: string;
	subProduct?: string;
};

/**
 * Manages state and callbacks for the Jira issue create form:
 * - Fires modal-view analytics once
 * - Fires analytics for the cancel, create, and back buttons
 */
export function useGicIssueCreateAnalytics({
	product,
	subProduct,
	isCrossAppCreate = false,
	isOpen,
}: UseGicIssueCreateAnalyticsParams) {
	const { fireObjectCreatedResult, fireFormControlAttributes, fireFlagControlAttributes } =
		usePlatformCreateAnalytics();
	const modalViewedRef = useRef(false);

	// Fire "create modal viewed" analytics once per mount.
	// Only fire when the modal is actually open.
	useEffect(
		function fireModalViewAnalytics() {
			if (!isOpen) {
				return;
			}

			if (!modalViewedRef.current) {
				fireFormControlAttributes({
					...getBaseFormControlAnalyticsAttributes({
						itemAti: ATI_PRESETS.WORK_ITEM,
						sourceProduct: product,
						sourceSubProduct: subProduct,
					}),
					itemProduct: JIRA_ITEM_PRODUCT,
					action: 'viewed',
					actionSubjectId: 'createModal',
					crossAppCreation: isCrossAppCreate,
				});
				modalViewedRef.current = true;
			}
		},
		[fireFormControlAttributes, isCrossAppCreate, isOpen, product, subProduct],
	);

	const fireCreateSuccessAnalytics = useCallback(() => {
		fireObjectCreatedResult({
			actionSubjectId: 'universalCreateGicCreation',
			result: 'success',
			itemAti: ATI_PRESETS.WORK_ITEM,
			crossAppCreation: isCrossAppCreate,
			sourceProduct: product,
			sourceSubProduct: subProduct,
		});
	}, [fireObjectCreatedResult, product, isCrossAppCreate, subProduct]);

	const fireCreateFailureAnalytics = useCallback(() => {
		fireObjectCreatedResult({
			actionSubjectId: 'universalCreateGicCreation',
			result: 'failed',
			itemAti: ATI_PRESETS.WORK_ITEM,
			crossAppCreation: isCrossAppCreate,
			sourceProduct: product,
			sourceSubProduct: subProduct,
		});
	}, [fireObjectCreatedResult, product, isCrossAppCreate, subProduct]);

	const fireModalCloseAnalytics = useCallback(() => {
		fireFormControlAttributes({
			...getBaseFormControlAnalyticsAttributes({
				itemAti: ATI_PRESETS.WORK_ITEM,
				sourceProduct: product,
				sourceSubProduct: subProduct,
			}),
			action: 'dismissed',
			itemProduct: JIRA_ITEM_PRODUCT,
			actionSubjectId: 'createGicModal',
			crossAppCreation: isCrossAppCreate,
		});
	}, [fireFormControlAttributes, isCrossAppCreate, product, subProduct]);

	const fireGicCloseAnalytics = useCallback(
		(isCreateSuccess?: boolean) => {
			fireFormControlAttributes({
				...getBaseFormControlAnalyticsAttributes({
					itemAti: ATI_PRESETS.WORK_ITEM,
					sourceProduct: product,
					sourceSubProduct: subProduct,
				}),
				action: 'dismissed',
				itemProduct: JIRA_ITEM_PRODUCT,
				actionSubjectId: 'createGicForm',
				crossAppCreation: isCrossAppCreate,
				isCreateSuccess,
			});
		},
		[fireFormControlAttributes, isCrossAppCreate, product, subProduct],
	);

	const fireOpenInJiraClickAnalytics = useCallback(() => {
		fireFlagControlAttributes({
			...getBaseFormControlAnalyticsAttributes({
				itemAti: ATI_PRESETS.WORK_ITEM,
				sourceProduct: product,
				sourceSubProduct: subProduct,
			}),
			itemProduct: JIRA_ITEM_PRODUCT,
			actionSubjectId: 'createModalOpenInJiraLink',
			crossAppCreation: isCrossAppCreate,
			flagName: 'issueCreateSuccess',
		});
	}, [fireFlagControlAttributes, isCrossAppCreate, product, subProduct]);

	const fireCopyLinkClickAnalytics = useCallback(() => {
		fireFlagControlAttributes({
			...getBaseFormControlAnalyticsAttributes({
				itemAti: ATI_PRESETS.WORK_ITEM,
				sourceProduct: product,
				sourceSubProduct: subProduct,
			}),
			itemProduct: JIRA_ITEM_PRODUCT,
			actionSubjectId: 'createModalCopyLinkButton',
			crossAppCreation: isCrossAppCreate,
			flagName: 'issueCreateSuccess',
		});
	}, [fireFlagControlAttributes, isCrossAppCreate, product, subProduct]);

	return {
		fireCreateSuccessAnalytics,
		fireCreateFailureAnalytics,
		fireModalCloseAnalytics,
		fireGicCloseAnalytics,
		fireCopyLinkClickAnalytics,
		fireOpenInJiraClickAnalytics,
	};
}
